// src/components/SettingsMenu.js
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiSettings, FiLogOut, FiUser, FiVolume2, FiVolumeX } from "react-icons/fi";
import { FaCrown } from "react-icons/fa";
import { signOutUser } from "../api/supabaseData";
import TitleGallery from "./TitleGallery";
import {
  getSoundPreferences,
  playSystemSound,
  setSoundMuted,
  setSoundVolume,
  subscribeToSoundPreferences,
} from "../audio/soundManager";

const SettingsMenu = ({ userLevel = 1 }) => {
  const [open, setOpen] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [soundPrefs, setSoundPrefs] = useState(() => getSoundPreferences());
  const ref = useRef(null);

  // Close on outside click or Esc
  useEffect(() => {
    const unsubscribe = subscribeToSoundPreferences((nextPrefs) => {
      setSoundPrefs(nextPrefs);
    });

    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      unsubscribe();
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const onLogout = async () => {
    try {
      await signOutUser();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      {/* Top-right settings button + dropdown */}
      <div className="settings-menu" ref={ref}>
        <button
          className="settings-btn"
          aria-haspopup="menu"
          aria-expanded={open}
          title="Settings"
          onClick={() => {
            playSystemSound("uiClick");
            setOpen((v) => !v);
          }}
        >
          <FiSettings size={20} />
        </button>

        {open && (
          <div className="settings-dropdown" role="menu">
            <Link
              className="settings-item"
              role="menuitem"
              to="/profile"
              onClick={() => setOpen(false)}
            >
              <FiUser style={{ marginRight: 8 }} />
              Hunter Profile
            </Link>

            <button
              className="settings-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                playSystemSound("questOpen");
                setShowGallery(true);
              }}
            >
              <FaCrown style={{ color: "#ffd700", marginRight: 8 }} />
              Title Gallery
            </button>

            <Link
              className="settings-item"
              role="menuitem"
              to="/leaderboard"
              onClick={() => setOpen(false)}
            >
              🏆 Leaderboard
            </Link>

            <div className="settings-sep" />

            <div className="settings-audio" role="group" aria-label="System sound controls">
              <div className="settings-audio__header">
                <div className="settings-audio__label">[ SYSTEM AUDIO ]</div>
                <button
                  type="button"
                  className={`settings-audio__toggle ${soundPrefs.muted ? "is-muted" : ""}`}
                  onClick={() => {
                    playSystemSound("uiClick");
                    setSoundMuted(!soundPrefs.muted);
                  }}
                  aria-pressed={!soundPrefs.muted}
                  title={soundPrefs.muted ? "Enable System audio" : "Mute System audio"}
                >
                  {soundPrefs.muted ? <FiVolumeX /> : <FiVolume2 />}
                  <span>{soundPrefs.muted ? "Muted" : "Active"}</span>
                </button>
              </div>

              <label className="settings-audio__slider-label" htmlFor="settings-audio-volume">
                Output Level
              </label>
              <input
                id="settings-audio-volume"
                className="settings-audio__slider"
                type="range"
                min="0"
                max="100"
                step="1"
                value={Math.round((soundPrefs.volume || 0) * 100)}
                onChange={(event) => {
                  setSoundVolume(Number(event.target.value) / 100);
                }}
              />
            </div>

            <div className="settings-sep" />

            <button className="settings-item danger" role="menuitem" onClick={onLogout}>
              <FiLogOut style={{ marginRight: 8 }} />
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Title Gallery modal (Solo-style; single frame) */}
      {showGallery && (
        <div className="solo-modal-bg" onClick={() => setShowGallery(false)}>
          <div className="solo-modal title-gallery-modal" onClick={(e) => e.stopPropagation()}>
            <TitleGallery userLevel={userLevel} onClose={() => setShowGallery(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default SettingsMenu;
